<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { CloseIcon } from '@solar-icons/vue/linear'
import AppMeter from '../../primitives/AppMeter/AppMeter.vue'
import type { PromptImageAttachment } from '@/composables/usePromptImageAttachments'

defineProps<{
  images: PromptImageAttachment[]
}>()

const emit = defineEmits<{
  remove: [id: string]
}>()

const { t } = useI18n()
</script>

<template>
  <TransitionGroup
    v-if="images.length"
    tag="div"
    name="prompt-chip"
    appear
    class="flex flex-wrap gap-6"
  >
    <div v-for="image in images" :key="image.id" class="icon-xs relative">
      <img
        :src="image.src"
        :alt="image.alt"
        class="size-24 rounded-4 bg-bg-surface object-cover inset-ring-1 inset-ring-border"
      />
      <div v-if="image.uploading" class="absolute inset-x-2 bottom-2">
        <AppMeter :label="t('chat.composer.uploadingImage')" :value="image.fraction" />
      </div>
      <button
        type="button"
        class="absolute -top-2 -right-2 grid size-8 place-items-center rounded-full bg-bg-primary text-text-secondary shadow-1 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue"
        :aria-label="t('chat.composer.removeImage')"
        @click="emit('remove', image.id)"
      >
        <CloseIcon />
      </button>
    </div>
  </TransitionGroup>
</template>

<style>
.prompt-chip-enter-active {
  transition:
    opacity var(--transition-duration-slow) var(--ease-spring),
    scale var(--transition-duration-slow) var(--ease-spring);
}
.prompt-chip-leave-active {
  transition:
    opacity var(--transition-duration-fast) var(--ease-soft-exit),
    scale var(--transition-duration-fast) var(--ease-soft-exit);
}
.prompt-chip-enter-from,
.prompt-chip-leave-to {
  opacity: 0;
  scale: 0.88;
}
@media (prefers-reduced-motion: reduce) {
  .prompt-chip-enter-from,
  .prompt-chip-leave-to {
    scale: none;
  }
}
</style>
